import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Reveal } from "./Motion";

const FAQS = [
    {
        q: "Are your paper bowls food-safe?",
        a: "Yes. Every bowl we ship carries either a food-safe PE coating or a water-based Aqueous coating. Both are approved for direct food contact and tested to hold hot and cold applications without failure.",
    },
    {
        q: "What is the difference between PE and Aqueous coating?",
        a: "PE coating is a thin polyethylene film laminated onto the paperboard — excellent for hot, oily and long-shelf-life food. Aqueous coating is a water-based barrier applied without any plastic film — better suited to cold, dry and short-transit food, and easier to compost through industrial facilities.",
    },
    {
        q: "Do you use PLA?",
        a: "No. We do not use PLA blends anywhere in our product range. Our substrate is virgin kraft or ECF-bleached paperboard, and our coatings are either PE or Aqueous — nothing else.",
    },
    {
        q: "Can I get custom printing on the bowls?",
        a: "Yes. We offer custom printing on all paper products with an MOQ of 20,000 units per SKU. We accept vector artwork (.ai/.pdf) in CMYK. Turnaround is typically 10–14 working days from plate approval.",
    },
    {
        q: "What is the minimum order quantity?",
        a: "MOQ is 10,000 units per SKU for salad and soup bowls, and 20,000 units per SKU for PET flat lids and custom-printed variants. Sample kits are available without a minimum.",
    },
    {
        q: "Do you ship outside India?",
        a: "Yes. We export to the GCC, EU and UK from our Bangalore factory. Container-ready pallet configurations are available with full documentation for HS 4823.69 / 4823.70 classifications.",
    },
    {
        q: "Where is your factory located?",
        a: "The Puro Pack is manufactured in Bangalore, Karnataka. We ship domestically within 5 days and internationally within 14 days from ex-factory dispatch.",
    },
];

export default function Faq({
    faqs = FAQS,
    kicker = "[ Frequently Asked ]",
    heading = (
        <>
            The <em className="italic">honest</em> answers.
        </>
    ),
}) {
    const [open, setOpen] = useState(0);
    const schema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
    };
    return (
        <section className="max-w-[1600px] mx-auto px-6 lg:px-10 py-24 lg:py-32" data-testid="faq-section">
            <script type="application/ld+json">{JSON.stringify(schema)}</script>
            <Reveal>
                <div className="grid lg:grid-cols-12 gap-8 mb-14">
                    <div className="lg:col-span-4 font-mono text-xs uppercase tracking-widest text-clay">{kicker}</div>
                    <h2 className="lg:col-span-8 font-serif text-5xl lg:text-7xl leading-[1] tracking-tight text-forest">
                        {heading}
                    </h2>
                </div>
            </Reveal>
            <div className="max-w-4xl mx-auto">
                {faqs.map((f, i) => {
                    const isOpen = open === i;
                    return (
                        <div key={i} className="border-t border-forest/30" data-testid={`faq-${i}`}>
                            <button
                                onClick={() => setOpen(isOpen ? -1 : i)}
                                aria-expanded={isOpen}
                                data-testid={`faq-toggle-${i}`}
                                className="w-full py-6 flex items-start justify-between gap-6 text-left group"
                            >
                                <h3 className="font-serif text-2xl md:text-3xl text-forest tracking-tight">{f.q}</h3>
                                <ChevronDown className={`w-5 h-5 mt-2 shrink-0 text-forest transition-transform ${isOpen ? "rotate-180" : ""}`} />
                            </button>
                            {isOpen && <div className="pb-6 max-w-3xl text-inkgreen/80 leading-relaxed">{f.a}</div>}
                        </div>
                    );
                })}
                <div className="border-t border-forest/30 h-px" />
            </div>
        </section>
    );
}
